import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Label,
  Option,
  Switch,
  useId,
} from '@fluentui/react-components';
import { ShareScope, ShareType } from '@mnspro-cloud/services';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalizedDatePicker } from '..';
import { Button } from '../Button/Button';
import { Input } from '../Input';

export interface FileCreateShareLinkDialogProps {
  onConfirmClick: (
    type: ShareType,
    scope: ShareScope,
    expirationDate: Date | null,
    isPasswordProtected: boolean,
    password: string | null,
  ) => void;
  onCancelClick: () => void;
}

/**
 * Dialog for settings options of a new share link for drive files.
 */
export const FileCreateShareLinkDialog: React.FC<FileCreateShareLinkDialogProps> = ({
  onCancelClick,
  onConfirmClick,
}) => {
  const { t } = useTranslation();
  const headerId = useId('fileCreateShareLinkDialogHeader');
  const scopeId = useId('fileShareScopeDropdown');
  const typeId = useId('fileShareTypeDropdown');
  const password1Id = useId('fileSharePassword');
  const password2Id = useId('fileSharePasswordRepeat');

  const [scope, setScope] = useState<ShareScope>(ShareScope.Organization);
  const [type, setType] = useState<ShareType>(ShareType.View);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [isPasswordProtected, setIsPasswordProtected] = useState<boolean>(false);
  const [password1, setPassword1] = useState<string>('');
  const [password2, setPassword2] = useState<string>('');

  const scopeMenuItems = [
    {
      key: ShareScope.Organization,
      header: t('fileshare_scope_organization', { defaultValue: 'Organisation' }),
      value: ShareScope.Organization,
    },
    {
      key: ShareScope.Anonymous,
      header: t('fileshare_scope_anonymous', { defaultValue: 'Jedem' }),
      value: ShareScope.Anonymous,
    },
  ];

  const typeMenuItems = [
    {
      key: ShareType.View,
      header: t('fileshare_type_view', { defaultValue: 'Lesen' }),
      value: ShareType.View,
    },
    {
      key: ShareType.Edit,
      header: t('fileshare_type_edit', { defaultValue: 'Lesen/Schreiben' }),
      value: ShareType.Edit,
    },
  ];

  const selectedScopeItem = scopeMenuItems.find((i) => i.value === scope);
  const selectedTypeItem = typeMenuItems.find((i) => i.value === type);

  const handleScopeChange = (newScope: ShareScope) => {
    setScope(newScope);

    if (newScope === ShareScope.Anonymous) {
      setExpirationDate(moment().add(1, 'day').toDate());
    } else {
      setExpirationDate(null);
      setIsPasswordProtected(false);
    }
  };

  const handleConfirmBtnClick = () => {
    onConfirmClick(
      type,
      scope,
      expirationDate,
      isPasswordProtected,
      isPasswordProtected ? password1 : null,
    );
  };

  const isConfirmBtnDisabled = (): boolean => {
    if (isPasswordProtected) {
      if (password1.trim() === '' || password2.trim() === '') {
        return true;
      }

      if (password1.trim() !== password2.trim()) {
        return true;
      }
    }

    return false;
  };

  const minDate = moment().add(1, 'day').toDate();

  return (
    <Dialog open={true}>
      <DialogSurface aria-labelledby={headerId}>
        <DialogBody>
          <DialogTitle id={headerId}>
            {t('fileshare_dialog_header', { defaultValue: 'Datei freigeben' })}
          </DialogTitle>
          <DialogContent>
            <div className="flex flex-col items-stretch w-full gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor={scopeId} weight="semibold">
                  {t('fileshare_scope_label', { defaultValue: 'Teilen mit' })}
                </Label>
                <Dropdown
                  appearance="filled-darker"
                  id={scopeId}
                  value={selectedScopeItem?.header ?? ''}
                  selectedOptions={selectedScopeItem ? [selectedScopeItem.value] : []}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      handleScopeChange(data.optionValue as ShareScope);
                    }
                  }}
                >
                  {scopeMenuItems.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.header}
                    </Option>
                  ))}
                </Dropdown>
              </div>

              <div className="flex flex-col gap-1">
                <Label htmlFor={typeId} weight="semibold">
                  {t('fileshare_type_label', { defaultValue: 'Rechte' })}
                </Label>
                <Dropdown
                  appearance="filled-darker"
                  id={typeId}
                  value={selectedTypeItem?.header ?? ''}
                  selectedOptions={selectedTypeItem ? [selectedTypeItem.value] : []}
                  onOptionSelect={(_event, data) => {
                    if (data.optionValue) {
                      setType(data.optionValue as ShareType);
                    }
                  }}
                >
                  {typeMenuItems.map((item) => (
                    <Option key={item.value} value={item.value}>
                      {item.header}
                    </Option>
                  ))}
                </Dropdown>
              </div>

              {expirationDate !== null && (
                <div className="flex flex-col gap-1">
                  <Label weight="semibold">
                    {t('fileshare_expiration_label', { defaultValue: 'Gültig bis' })}
                  </Label>
                  <LocalizedDatePicker
                    allowManualInput={false}
                    selectedDate={expirationDate}
                    minDate={minDate}
                    onDateChange={(_, props) => setExpirationDate(props?.value ?? null)}
                  />
                </div>
              )}

              {scope === ShareScope.Anonymous && (
                <Switch
                  checked={isPasswordProtected}
                  label={t('fileshare_use_password_protection', {
                    defaultValue: 'Zugriff mit einem Passwort schützen',
                  })}
                  onChange={(_event, data) => setIsPasswordProtected(data.checked)}
                />
              )}
              {isPasswordProtected && (
                <>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={password1Id} weight="semibold">
                      {t('fileshare_password_label', { defaultValue: 'Passwort' })}
                    </Label>
                    <Input
                      appearance="filled-darker"
                      id={password1Id}
                      type="password"
                      value={password1}
                      onChange={(event) => setPassword1(event.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Label htmlFor={password2Id} weight="semibold">
                      {t('fileshare_repeat_password_label', {
                        defaultValue: 'Passwort wiederholen',
                      })}
                    </Label>
                    <Input
                      appearance="filled-darker"
                      id={password2Id}
                      type="password"
                      value={password2}
                      onChange={(event) => setPassword2(event.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancelClick}>{t('cancel', { defaultValue: 'Abbrechen' })}</Button>
            <Button
              appearance="primary"
              disabled={isConfirmBtnDisabled()}
              onClick={handleConfirmBtnClick}
            >
              {t('fileshare_confirm_btn', { defaultValue: 'Datei freigeben' })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
